/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Rich Text Editor Top Formatting Toolbar (Task 10.2.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import type { Editor } from '@tiptap/react';
import { useI18nStore } from '../../stores/useI18nStore';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Code,
  Highlighter,
  Link as LinkIcon,
  Unlink,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  CheckSquare,
  Quote,
  Code2,
  Table as TableIcon,
  Plus,
  Trash2,
  Undo2,
  Redo2,
  ImageIcon,
  Minus,
} from 'lucide-react';

export interface EditorToolbarProps {
  editor: Editor | null;
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({ editor }) => {
  const { t } = useI18nStore();
  const [showTableMenu, setShowTableMenu] = useState(false);

  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt(t('editor.toolbar.promptLink'), previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt(t('editor.toolbar.promptImage'));
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const isTableActive = editor.isActive('table');

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 rounded-xl border border-surface-border bg-surface shadow-sm text-foreground">
      {/* 1. History Undo / Redo */}
      <div className="flex items-center gap-0.5 pr-1 border-r border-surface-border">
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted disabled:opacity-40 transition-colors"
          title={t('editor.toolbar.undo')}
        >
          <Undo2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted disabled:opacity-40 transition-colors"
          title={t('editor.toolbar.redo')}
        >
          <Redo2 className="w-4 h-4" />
        </button>
      </div>

      {/* 2. Heading Selectors */}
      <div className="flex items-center gap-0.5 px-1 border-r border-surface-border">
        <button
          type="button"
          onClick={() => editor.chain().focus().setParagraph().run()}
          className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
            editor.isActive('paragraph') && !editor.isActive('heading')
              ? 'bg-primary/15 text-primary font-bold'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.paragraph')}
        >
          P
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
            editor.isActive('heading', { level: 1 })
              ? 'bg-primary/15 text-primary font-bold'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.heading1')}
        >
          H1
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
            editor.isActive('heading', { level: 2 })
              ? 'bg-primary/15 text-primary font-bold'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.heading2')}
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
            editor.isActive('heading', { level: 3 })
              ? 'bg-primary/15 text-primary font-bold'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.heading3')}
        >
          H3
        </button>
      </div>

      {/* 3. Text Formatting (Bold, Italic, Underline, Strike, Code, Highlight) */}
      <div className="flex items-center gap-0.5 px-1 border-r border-surface-border">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('bold')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.bold')}
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('italic')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.italic')}
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('underline')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.underline')}
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('strike')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.strike')}
        >
          <Strikethrough className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('code')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.code')}
        >
          <Code className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('highlight')
              ? 'bg-amber-500/20 text-amber-500'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.highlight')}
        >
          <Highlighter className="w-4 h-4" />
        </button>
      </div>

      {/* 4. Link & Image */}
      <div className="flex items-center gap-0.5 px-1 border-r border-surface-border">
        <button
          type="button"
          onClick={setLink}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('link')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.setLink')}
        >
          <LinkIcon className="w-4 h-4" />
        </button>
        {editor.isActive('link') && (
          <button
            type="button"
            onClick={() => editor.chain().focus().unsetLink().run()}
            className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
            title={t('editor.toolbar.unsetLink')}
          >
            <Unlink className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={addImage}
          className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
          title={t('editor.toolbar.addImage')}
        >
          <ImageIcon className="w-4 h-4" />
        </button>
      </div>

      {/* 5. Text Alignments */}
      <div className="flex items-center gap-0.5 px-1 border-r border-surface-border">
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive({ textAlign: 'left' })
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.alignLeft')}
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive({ textAlign: 'center' })
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.alignCenter')}
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive({ textAlign: 'right' })
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.alignRight')}
        >
          <AlignRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive({ textAlign: 'justify' })
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.alignJustify')}
        >
          <AlignJustify className="w-4 h-4" />
        </button>
      </div>

      {/* 6. Lists, Quotes & CodeBlock */}
      <div className="flex items-center gap-0.5 px-1 border-r border-surface-border">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('bulletList')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.bulletList')}
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('orderedList')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.orderedList')}
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('taskList')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.taskList')}
        >
          <CheckSquare className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('blockquote')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.blockquote')}
        >
          <Quote className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`p-1.5 rounded-lg transition-colors ${
            editor.isActive('codeBlock')
              ? 'bg-primary/15 text-primary'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.codeBlock')}
        >
          <Code2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
          title={t('editor.toolbar.horizontalRule')}
        >
          <Minus className="w-4 h-4" />
        </button>
      </div>

      {/* 7. Table Operations */}
      <div className="relative flex items-center gap-0.5 pl-1">
        <button
          type="button"
          onClick={() => {
            if (!isTableActive) {
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run();
            } else {
              setShowTableMenu(!showTableMenu);
            }
          }}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
            isTableActive
              ? 'bg-primary text-primary-fg'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted'
          }`}
          title={t('editor.toolbar.tableMenu')}
        >
          <TableIcon className="w-4 h-4" />
          <span>{t('editor.toolbar.table')}</span>
        </button>

        {isTableActive && showTableMenu && (
          <div className="absolute top-full right-0 mt-1 z-30 w-44 rounded-xl border border-surface-border bg-surface p-1 shadow-glass text-xs space-y-0.5 animate-scale-in">
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().addRowAfter().run();
                setShowTableMenu(false);
              }}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left hover:bg-muted"
            >
              <Plus className="w-3.5 h-3.5" /> {t('editor.toolbar.addRowAfter')}
            </button>
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().addColumnAfter().run();
                setShowTableMenu(false);
              }}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left hover:bg-muted"
            >
              <Plus className="w-3.5 h-3.5" /> {t('editor.toolbar.addColumnAfter')}
            </button>
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().deleteRow().run();
                setShowTableMenu(false);
              }}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left hover:bg-muted text-red-500"
            >
              <Trash2 className="w-3.5 h-3.5" /> {t('editor.toolbar.deleteRow')}
            </button>
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().deleteColumn().run();
                setShowTableMenu(false);
              }}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left hover:bg-muted text-red-500"
            >
              <Trash2 className="w-3.5 h-3.5" /> {t('editor.toolbar.deleteColumn')}
            </button>
            <div className="border-t border-surface-border my-1" />
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().deleteTable().run();
                setShowTableMenu(false);
              }}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left hover:bg-red-500/10 text-red-600 font-medium"
            >
              <Trash2 className="w-3.5 h-3.5" /> {t('editor.toolbar.deleteTable')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
